import { useEffect, useState, useCallback } from "react";
import { Plus, Tag, Ticket, Pencil } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const yen = (n) => `¥${Number(n || 0).toLocaleString()}`;
const EMPTY_PLAN = { lookup_key: "", name: "", price: 9800, interval: "月", features: [], active: true };
const EMPTY_COUPON = { code: "", percent_off: 10, active: true };

export default function PlatformPlans() {
  const [plans, setPlans] = useState(null);
  const [coupons, setCoupons] = useState(null);
  const [pOpen, setPOpen] = useState(false);
  const [cOpen, setCOpen] = useState(false);
  const [plan, setPlan] = useState(EMPTY_PLAN);
  const [coupon, setCoupon] = useState(EMPTY_COUPON);
  const [isNewPlan, setIsNewPlan] = useState(true);

  const load = useCallback(() => {
    api.get("/platform/plans").then(({ data }) => setPlans(data));
    api.get("/platform/coupons").then(({ data }) => setCoupons(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPlan = (p) => { setIsNewPlan(!p); setPlan(p ? { ...p, features: p.features || [] } : EMPTY_PLAN); setPOpen(true); };
  const savePlan = async () => {
    if (!plan.lookup_key || !plan.name) { toast.error("キーと名称は必須です"); return; }
    try {
      await api.post("/platform/plans", { ...plan, price: Number(plan.price), features: (plan.featuresText != null ? plan.featuresText.split("\n") : plan.features).filter(Boolean) });
      toast.success("プランを保存しました"); setPOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openCoupon = (c) => { setCoupon(c ? { ...c } : EMPTY_COUPON); setCOpen(true); };
  const saveCoupon = async () => {
    if (!coupon.code) { toast.error("クーポンコードは必須です"); return; }
    try {
      await api.post("/platform/coupons", { ...coupon, percent_off: Number(coupon.percent_off) });
      toast.success("クーポンを保存しました"); setCOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!plans || !coupons) return <Loading />;

  return (
    <div data-testid="platform-plans">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">プラン・料金</h1>
        <p className="text-sm text-slate-400 mt-1">サブスクリプションプランとクーポンの管理</p>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-200 flex items-center gap-2"><Tag className="w-4 h-4 text-amber-400" /> プラン</h3>
        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={() => openPlan(null)} data-testid="add-plan-btn"><Plus className="w-4 h-4 mr-1" />プラン追加</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {plans.map((p) => (
          <div key={p.lookup_key} className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid={`plan-card-${p.lookup_key}`}>
            <div className="flex items-start justify-between">
              <div className="font-semibold text-slate-100">{p.name}</div>
              <button onClick={() => openPlan(p)} className="text-slate-500 hover:text-amber-400" data-testid={`edit-plan-${p.lookup_key}`}><Pencil className="w-4 h-4" /></button>
            </div>
            <div className="mt-2 text-2xl font-bold text-white">{yen(p.price)}<span className="text-sm text-slate-500 font-normal"> /{p.interval}</span></div>
            <div className="mt-1 text-xs text-slate-500">{p.lookup_key}</div>
            <span className={`inline-flex mt-3 px-2 py-0.5 rounded-full text-xs ${p.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>{p.active ? "有効" : "無効"}</span>
          </div>
        ))}
        {!plans.length && <p className="text-sm text-slate-500">プランがありません</p>}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-200 flex items-center gap-2"><Ticket className="w-4 h-4 text-amber-400" /> クーポン</h3>
        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={() => openCoupon(null)} data-testid="add-coupon-btn"><Plus className="w-4 h-4 mr-1" />クーポン追加</Button>
      </div>
      <div className="bg-slate-900 rounded-xl border border-slate-800 divide-y divide-slate-800">
        {coupons.map((c) => (
          <div key={c.code} className="flex items-center justify-between p-4" data-testid={`coupon-row-${c.code}`}>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm bg-slate-800 text-amber-400 px-2 py-0.5 rounded">{c.code}</span>
              <span className="text-slate-300 text-sm">{c.percent_off}% OFF</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>{c.active ? "有効" : "無効"}</span>
            </div>
            <button onClick={() => openCoupon(c)} className="text-slate-500 hover:text-amber-400" data-testid={`edit-coupon-${c.code}`}><Pencil className="w-4 h-4" /></button>
          </div>
        ))}
        {!coupons.length && <div className="p-6 text-sm text-slate-500 text-center">クーポンがありません</div>}
      </div>

      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isNewPlan ? "プラン追加" : "プラン編集"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>プランキー（lookup_key）*</Label><Input value={plan.lookup_key} disabled={!isNewPlan} onChange={(e) => setPlan({ ...plan, lookup_key: e.target.value })} placeholder="wcc_standard_monthly" className="mt-1" data-testid="plan-key" /></div>
            <div><Label>プラン名 *</Label><Input value={plan.name} onChange={(e) => setPlan({ ...plan, name: e.target.value })} className="mt-1" data-testid="plan-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>価格（円）</Label><Input type="number" value={plan.price} onChange={(e) => setPlan({ ...plan, price: e.target.value })} className="mt-1" data-testid="plan-price" /></div>
              <div>
                <Label>請求周期</Label>
                <Select value={plan.interval} onValueChange={(v) => setPlan({ ...plan, interval: v })}>
                  <SelectTrigger className="mt-1" data-testid="plan-interval"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="月">月額</SelectItem><SelectItem value="年">年額</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>特徴（1行に1項目）</Label><Textarea value={plan.featuresText != null ? plan.featuresText : (plan.features || []).join("\n")} onChange={(e) => setPlan({ ...plan, featuresText: e.target.value })} className="mt-1" rows={3} data-testid="plan-features" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={plan.active} onCheckedChange={(c) => setPlan({ ...plan, active: !!c })} data-testid="plan-active" /> 有効（申込・表示可能）</label>
          </div>
          <DialogFooter><Button onClick={savePlan} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-plan-btn">保存</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>クーポン</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>クーポンコード *</Label><Input value={coupon.code} onChange={(e) => setCoupon({ ...coupon, code: e.target.value.toUpperCase() })} className="mt-1" data-testid="coupon-code" /></div>
            <div><Label>割引率（%）</Label><Input type="number" value={coupon.percent_off} onChange={(e) => setCoupon({ ...coupon, percent_off: e.target.value })} className="mt-1" data-testid="coupon-percent" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={coupon.active} onCheckedChange={(c) => setCoupon({ ...coupon, active: !!c })} data-testid="coupon-active" /> 有効</label>
          </div>
          <DialogFooter><Button onClick={saveCoupon} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-coupon-btn">保存</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
